import { DateTime } from 'luxon';

import { chartOfAccountsTypes } from '../chart-of-accounts';
import { transactionsTransformers } from '../transactions';
import { splitItemsToArray } from '../transactions/splitsHelpers';
import type { TransactionProps } from '../transactions/transactionsTypes';
import type { CreateBillScoutSchedulePayload, CreateBillScoutScheduleRequest } from './scheduledTransactionsActions';
import {
  mkScheduledTransaction,
  type ScheduledTransaction,
  type ScheduledTransactionType,
} from './scheduledTransactionsTypes';

export const transformBillScoutScheduleToRequestData = (
  resource: CreateBillScoutSchedulePayload,
): CreateBillScoutScheduleRequest => ({
  clientId: resource.clientId,
  ...(resource.schedule && { schedule: { isUserVerified: resource.schedule.isUserVerified } }),
});

// QCS rejects a CATEGORY coa without a real id on every write, and stores a create sent with no coa and no
// splits as exactly that.
const needsUncategorizedCoa = ({ coa, split }: Pick<TransactionProps, 'coa' | 'split'>, isCreate: boolean): boolean =>
  coa
    ? coa.type === 'CATEGORY' && (!coa.id || coa.id === '0')
    : isCreate && splitItemsToArray(split?.items).length === 0;

// request transform
export const transformScheduledTransactionToRequestData = (
  resource: ScheduledTransaction | ScheduledTransactionType | null | undefined,
) => {
  // `isExcludedFromF2S` on the nested transaction must match the series-level `excludeF2S`.
  // Mismatched values (e.g. excludeF2S:'NONE' + isExcludedFromF2S:true) cause the API to
  // return QCS-0400-9. SRC and BOTH mean the source side is excluded from F2S.
  const txnRequestData = resource?.transaction
    ? transactionsTransformers.transformTransactionToRequestData(resource.transaction)
    : undefined;
  const syncedTxnRequestData =
    txnRequestData && resource?.excludeF2S != null
      ? { ...txnRequestData, isExcludedFromF2S: resource.excludeF2S === 'SRC' || resource.excludeF2S === 'BOTH' }
      : txnRequestData;
  const transactionRequestData =
    syncedTxnRequestData && needsUncategorizedCoa(syncedTxnRequestData, !resource?.id)
      ? { ...syncedTxnRequestData, coa: { ...chartOfAccountsTypes.UNCATEGORIZED_COA } }
      : syncedTxnRequestData;

  const updatedatedResource = {
    ...(resource && 'toJS' in resource ? (resource.toJS() as ScheduledTransactionType) : resource),
    ...(resource?.recurrence && {
      recurrence: {
        ...(('toJS' in resource.recurrence && resource.recurrence?.toJS?.()) || resource.recurrence),
        endOn: DateTime.isDateTime(resource.recurrence.endOn)
          ? resource.recurrence.endOn.toISODate()
          : resource.recurrence.endOn,
      },
    }),
    ...(transactionRequestData && { transaction: transactionRequestData }),
    isUserVerified: resource?.isUserVerified ?? undefined,
    // Server-owned; omit it so the API leaves it unchanged instead of echoing a stale value.
    merchantId: undefined,
  } as ScheduledTransactionType;

  // byMonthDayOrLastDay has been deprecated in favor of byMonthDay, adjust any old recurrences or recurrrences from agggregation
  if (updatedatedResource.recurrence?.byMonthDayOrLastDay) {
    updatedatedResource.recurrence = {
      ...updatedatedResource.recurrence,
      byMonthDay:
        updatedatedResource.recurrence?.byMonthDay && updatedatedResource.recurrence?.byMonthDay.length > 0
          ? updatedatedResource.recurrence.byMonthDay.concat(updatedatedResource.recurrence.byMonthDayOrLastDay)
          : updatedatedResource.recurrence.byMonthDayOrLastDay,
      byMonthDayOrLastDay: undefined, // remove the old prop
      byDay: undefined, // remove conflicting prop
      byDates: undefined, // remove conflicting prop
    };
  }

  return updatedatedResource;
};

// The nested transaction template's own `source` can come back empty (or explicitly null) from
// the wire even for a bank-linked series; the schedule resource's own top-level `source`
// reliably carries it in that case, so fall back to it when the template itself has none
// (SIMPL-35684).
export const applyScheduleSourceFallback = <
  T extends { source?: TransactionProps['source']; transaction?: TransactionProps | null } | null | undefined,
>(
  data: T,
): TransactionProps | null | undefined =>
  data?.transaction && data.transaction.source == null && data.source != null
    ? { ...data.transaction, source: data.source }
    : data?.transaction;

// QCS leaves out null fields and false flags, so a field it clears is missing from the record and a merge would
// keep the cached value. Keep this list explicit: bumps drop fields the bump service's DTO doesn't know yet.
const SERVER_CLEARED_FIELD_DEFAULTS = {
  overrideNextDueOn: null,
  overrideNextAmount: null,
  autoAdjustAmount: null,
  autoAdjustDueOn: false,
  paymentProvider: null,
  providerBillerId: null,
  billPresentmentAccountId: null,
  billPresentmentBillerId: null,
  billPresentmentProvider: null,
  presentmentAccountId: null,
  billSnapshot: null,
  billerType: null,
  billerEnrollmentStatus: null,
  printCheck: false,
  matchAnyAmount: false,
  matchAmountVariance: null,
} satisfies ScheduledTransactionType;

// response transform
export const transformDataToScheduledTransaction = (
  data: { source?: TransactionProps['source']; transaction?: TransactionProps | null } | null | undefined,
) => {
  const transactionData = applyScheduleSourceFallback(data);

  return mkScheduledTransaction({
    ...SERVER_CLEARED_FIELD_DEFAULTS,
    ...data,
    ...{ type: undefined },
    transaction: transactionData
      ? transactionsTransformers.transformQcsTransactionToTransaction(transactionData)
      : null,
  });
};
